import { WHelper } from '@alice-health/wonderland-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useContext } from 'react'

import {
  useAnswerSimulation,
  useFinishSimulation,
  useGetUnifiedSimulatorEndpointFlag,
  useUpsertLead
} from '@/modules/simulator/hooks'
import { companyDataFormSchema } from '@/modules/simulator/schemas/CompanyDataFormSchema'
import {
  SimulatorContext,
  SimulatorDispatchContext
} from '@/modules/simulator/contexts'
import { useDispatchPageEvent, usePreventDoubleClick } from '@/global/hooks'
import {
  ButtonType,
  EventName
} from '@/global/utils/analytics-events/types/common'
import { dispatchEvent } from '@/global/utils'
import {
  CityField,
  CompanyCNPJField,
  CompanyNameField,
  FindOutAliceSelect,
  PhoneField,
  ResponsibleEmailField,
  ResponsibleNameField
} from '@/modules/simulator/components/Forms/Fields'
import { sp } from '@/modules/simulator/constants/city-options'
import { FormTemplate } from '@/modules/simulator/components/Forms/FormTemplate'
import {
  strings,
  buildPayload,
  dispatchAnalyticsEvents,
  checkIfFormIsFilled
} from '@/modules/simulator/components/Forms/CompanyDataForm/CompanyDataForm.helpers'
import { CustomFormProvider } from '@/modules/simulator/components/Forms/CustomFormProvider'
import { Form, CompanyDataFormProps } from './CompanyDataForm.types'
import { mlaCompanySize } from '@/modules/simulator/constants/company-size'
import { clearSimulationDataStorage } from '@/modules/simulator/functions'
import { useCreateSimulation } from '@/modules/simulator/hooks/useCreateSimulation'
import { lgpdConsentText } from '@/modules/simulator/constants/lgpd'

const CompanyDataForm = ({ onFinish }: CompanyDataFormProps) => {
  /**
   * Events
   */

  useDispatchPageEvent({
    eventName: EventName.PAGE_VIEWED,
    pageTitle: strings.title
  })

  /**
   * Contexts
   */

  const { simulation } = useContext(SimulatorContext)
  const { simulationDispatch, stepDispatch } = useContext(
    SimulatorDispatchContext
  )

  /**
   * Form
   */

  const methods = useForm<Form>({
    resolver: zodResolver(companyDataFormSchema),
    defaultValues: {
      responsibleName: '',
      telefone: '',
      responsibleEmail: '',
      companyName: '',
      cnpj: '',
      city: sp,
      findOutAliceSelect: '',
      socialMediaSelect: '',
      otherField: ''
    }
  })

  const { getValues, watch } = methods

  const formValues = watch()
  const isFormFilled = checkIfFormIsFilled(formValues)

  /**
   * Mutation callbacks
   */

  const onFinishSimulation = () => {
    const employeesNumber = simulation.answers.companyEmployees

    if (!employeesNumber) return

    const is30PlusEmployees = mlaCompanySize.includes(employeesNumber)

    if (is30PlusEmployees) upsertLead()
    else finishSimulation()
  }

  const onAnswerSuccess = () => {
    const data = getValues()
    const payload = buildPayload(data, simulation.answers)

    simulationDispatch({
      type: 'SET_ANSWER',
      payload
    })

    onFinishSimulation()
  }

  const onSuccess = () => {
    clearSimulationDataStorage()
    onFinish?.()
  }

  const onCreateSimulationSuccess = () => {
    const data = getValues()
    const payload = buildPayload(data, simulation.answers)

    simulationDispatch({
      type: 'SET_ANSWER',
      payload
    })

    onSuccess()
  }

  /**
   * Mutations and queries
   */

  const { value: isUnifiedSimulatorEndpointFlag } =
    useGetUnifiedSimulatorEndpointFlag()

  const { mutate: answer, isPending: isAnswerPending } = useAnswerSimulation({
    onSuccess: onAnswerSuccess
  })

  const { isPending: isUpsertLeadPending, mutate: upsertLead } = useUpsertLead({
    onSuccess: onSuccess
  })

  const { isPending: isFinishSimulationPending, mutate: finishSimulation } =
    useFinishSimulation({
      onSuccess: onSuccess
    })

  const { mutate: createSimulation, isPending: isCreateSimulationPending } =
    useCreateSimulation({
      onSuccess: onCreateSimulationSuccess
    })

  /**
   * Handlers
   */

  const onSubmit: SubmitHandler<Form> = usePreventDoubleClick(data => {
    dispatchAnalyticsEvents(simulation.answers.companyEmployees)

    const payload = buildPayload(data, simulation.answers)

    if (isUnifiedSimulatorEndpointFlag) {
      createSimulation(payload)
    } else {
      answer({
        id: simulation.id,
        payload
      })
    }
  })

  const onBack = usePreventDoubleClick(() => {
    simulationDispatch({
      type: 'RESET_COMPANY_DATA'
    })
    stepDispatch({ type: 'SET_STEP', payload: 1 })

    dispatchEvent({
      eventName: EventName.BUTTON_CLICKED,
      buttonType: ButtonType.NEW_PAGE,
      buttonName: 'Voltar'
    })
  })

  const loading =
    isAnswerPending ||
    isUpsertLeadPending ||
    isFinishSimulationPending ||
    isCreateSimulationPending

  return (
    <CustomFormProvider
      formMethods={methods}
      onSubmit={onSubmit}
    >
      <FormTemplate
        title={strings.title}
        accordionDescription={strings.accordionDescription}
        submitButtonLabel={strings.submitLabel}
        backButton
        disabled={!isFormFilled}
        loading={loading}
        onBack={onBack}
      >
        <div className='flex flex-col gap-gl-spacing-04'>
          <ResponsibleNameField />

          <PhoneField />

          <ResponsibleEmailField />

          <CompanyNameField />

          <CompanyCNPJField />

          <CityField />

          <FindOutAliceSelect />

          <WHelper mode='tertiary'>{lgpdConsentText}</WHelper>
        </div>
      </FormTemplate>
    </CustomFormProvider>
  )
}

export { CompanyDataForm }
